import React, { useEffect } from "react";
import {
  AppShell,
  Burger,
  Button,
  Group,
  NavLink,
  ScrollArea,
  Text,
  Collapse,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { Link, useLocation } from "react-router-dom";
import { IconBuildingStore, IconHistory, IconListDetails, IconLogout, IconUpload, IconChevronDown, IconHome, IconFlask, IconMessageCircleQuestion, IconLeaf } from "@tabler/icons-react";

import authService from "../services/authService";

type AdminLayoutProps = {
  children: React.ReactNode;
};

/**
 * Lightweight Admin shell.
 *
 * We intentionally keep this custom (instead of using Refine's Mantine UI package)
 * because this project uses Mantine v7, while Refine's Mantine UI integration
 * currently targets Mantine v5.
 */
export function AdminLayout({ children }: AdminLayoutProps) {
  const [opened, { toggle }] = useDisclosure();
  const [uploadMenuOpened, { toggle: toggleUploadMenu }] = useDisclosure(false);
  const location = useLocation();
  
  // Note: ColorSchemeManager in App.tsx handles all color scheme management
  // No need to set color scheme here - it would cause conflicts and flashing

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const isUploadActive = isActive("/admin/upload");

  // Auto-expand upload menu when on an upload page
  useEffect(() => {
    if (isUploadActive && !uploadMenuOpened) {
      toggleUploadMenu();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUploadActive]);

  const handleLogout = () => {
    authService.logout();
    // Let the router handle redirect via route guards.
    window.location.assign("/login");
  };

  return (
    <AppShell
      header={{ height: 56 }}
      navbar={{ width: 280, breakpoint: "sm", collapsed: { mobile: !opened } }}
      padding="md"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group>
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
            <Text fw={700}>Admin</Text>
          </Group>

          <Button
            variant="subtle"
            color="red"
            leftSection={<IconLogout size={16} />}
            onClick={handleLogout}
          >
            Log out
          </Button>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="xs">
        <AppShell.Section component={ScrollArea} grow>
          <NavLink
            component={Link}
            to="/admin"
            label="Dashboard"
            leftSection={<IconHome size={18} />}
            active={location.pathname === "/admin"}
          />
          <NavLink
            component={Link}
            to="/admin/restaurants"
            label="Restaurants"
            leftSection={<IconBuildingStore size={18} />}
            active={isActive("/admin/restaurants")}
          />
          <NavLink
            component={Link}
            to="/admin/menus"
            label="Menus"
            leftSection={<IconListDetails size={18} />}
            active={isActive("/admin/menus")}
          />
          <NavLink
            component={Link}
            to="/admin/menus/testing"
            label="Menu Q&A Testing"
            leftSection={<IconFlask size={18} />}
            active={location.pathname === "/admin/menus/testing"}
          />
          <NavLink
            component={Link}
            to="/admin/menu-item-qa"
            label="Menu Item FAQ"
            leftSection={<IconMessageCircleQuestion size={18} />}
            active={location.pathname === "/admin/menu-item-qa"}
          />
          <NavLink
            component={Link}
            to="/admin/ingredients"
            label="Ingredients"
            leftSection={<IconLeaf size={18} />}
            active={isActive("/admin/ingredients")}
          />
          <NavLink
            component={Link}
            to="/admin/query-history"
            label="Query history"
            leftSection={<IconHistory size={18} />}
            active={isActive("/admin/query-history")}
          />
          <NavLink
            label="Upload Menu"
            leftSection={<IconUpload size={18} />}
            rightSection={
              <IconChevronDown
                size={16}
                style={{
                  transform: uploadMenuOpened ? "rotate(-180deg)" : "none",
                  transition: "transform 200ms ease",
                }}
              />
            }
            active={isUploadActive}
            onClick={toggleUploadMenu}
          />
          <Collapse in={uploadMenuOpened}>
            <NavLink
              component={Link}
              to="/admin/upload/v2"
              label="Upload Menu by Photo"
              pl="xl"
              active={location.pathname === "/admin/upload/v2"}
            />
            <NavLink
              component={Link}
              to="/admin/upload/csv"
              label="CSV Upload"
              pl="xl"
              active={location.pathname === "/admin/upload/csv"}
            />
            <NavLink
              component={Link}
              to="/admin/upload/import-csv"
              label="Import CSV (No AI)"
              pl="xl"
              active={location.pathname === "/admin/upload/import-csv"}
            />
          </Collapse>
          <NavLink
            component={Link}
            to="/"
            label="Back to site"
          />
        </AppShell.Section>
      </AppShell.Navbar>

      <AppShell.Main>{children}</AppShell.Main>
    </AppShell>
  );
}
